import { ResponsiveActions } from "../../../../components/layout";
import AppIconButton from "../../../../components/form/AppIconButton";
import { type ReactNode } from "react";
import { Alert, Box, Chip, CircularProgress, List, ListItemButton, ListItemIcon, ListItemText, Paper, Stack, Typography } from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import PreviewOutlinedIcon from "@mui/icons-material/PreviewOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { useTranslation } from "react-i18next";
import { useAppCustomStyles } from "../../../../assets/theme/custom/hooks";
import { AppButton } from "../../../../components/form";
import { PageCommandBar, PageSectionCard } from "../../../../components/layout";
import type { WidgetEditSection } from "./types";

type NavItem = {
  id: WidgetEditSection;
  labelKey: string;
  icon: ReactNode;
};

type Props = {
  mode: "create" | "edit";
  title: string;
  widgetName?: string;
  outputFormatLabel?: string;
  statusChip?: ReactNode;
  errors: string[];
  saving?: boolean;
  previewing?: boolean;
  activeSection: WidgetEditSection;
  onSectionChange: (section: WidgetEditSection) => void;
  showHistoryNav?: boolean;
  onBack: () => void;
  onValidate: () => void;
  onPreview: () => void;
  onSave: () => void;
  content: ReactNode;
};

const NAV_ITEMS: NavItem[] = [
  { id: "overview", labelKey: "widgetEditStudio.nav.overview", icon: <ArticleOutlinedIcon fontSize="small" /> },
  { id: "data", labelKey: "widgetEditStudio.nav.data", icon: <StorageOutlinedIcon fontSize="small" /> },
  { id: "presentation", labelKey: "widgetEditStudio.nav.presentation", icon: <BarChartOutlinedIcon fontSize="small" /> },
  { id: "preview", labelKey: "widgetEditStudio.nav.preview", icon: <PreviewOutlinedIcon fontSize="small" /> },
  { id: "parameters", labelKey: "widgetEditStudio.nav.parameters", icon: <TuneOutlinedIcon fontSize="small" /> },
  { id: "code", labelKey: "widgetEditStudio.nav.code", icon: <CodeOutlinedIcon fontSize="small" /> },
  { id: "docs", labelKey: "widgetEditStudio.nav.docs", icon: <MenuBookOutlinedIcon fontSize="small" /> },
  { id: "history", labelKey: "widgetEditStudio.nav.history", icon: <HistoryOutlinedIcon fontSize="small" /> },
];

export function WidgetEditSectionCard({
  title,
  description,
  actions,
  children,
  noPadding,
  fillHeight,
}: {
  title?: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
  noPadding?: boolean;
  fillHeight?: boolean;
}) {
  return (
    <PageSectionCard
      title={title}
      description={description}
      actions={actions}
      noPadding={noPadding}
      fillHeight={fillHeight}
    >
      {children}
    </PageSectionCard>
  );
}

export default function WidgetEditStudioLayout({
  mode,
  title,
  widgetName,
  outputFormatLabel,
  statusChip,
  errors,
  saving,
  previewing,
  activeSection,
  onSectionChange,
  showHistoryNav,
  onBack,
  onValidate,
  onPreview,
  onSave,
  content,
}: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const isPreviewSection = activeSection === "preview";

  const navItems = NAV_ITEMS.filter((item) => !(item.id === "history" && !showHistoryNav));

  return (
    <Box
      sx={custom.components.widgets.studioRoot}
    >
      <PageCommandBar
        leading={
          <AppIconButton title={t("widgetEditStudio.back")} onClick={onBack}>
            <ArrowBackOutlinedIcon fontSize="small" />
          </AppIconButton>
        }
        title={widgetName?.trim() ? widgetName : title}
        subtitle={
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" color="text.secondary">
              {mode === "create" ? t("widget.create") : t("widget.edit")}
            </Typography>
            {outputFormatLabel && (
              <Chip size="small" label={outputFormatLabel} variant="outlined" />
            )}
            {statusChip}
          </Stack>
        }
        actions={
          <ResponsiveActions
            items={[
              { key: "cancel", label: t("common.cancel"), icon: "close", onClick: onBack },
              { key: "validate", label: t("widget.validate"), icon: "check_circle_outline", onClick: onValidate },
              {
                key: "preview",
                label: previewing ? t("widget.previewing") : t("widget.preview"),
                icon: "play_arrow",
                onClick: onPreview,
                disabled: previewing,
              },
              {
                key: "save",
                label: t("common.save"),
                icon: "save",
                onClick: onSave,
                disabled: saving,
                primary: true,
              },
            ]}
          />
        }
      />

      {errors.length > 0 && (
        <Alert severity="error" sx={custom.components.widgets.studioErrorAlert}>
          {errors.join("; ")}
        </Alert>
      )}

      <Box
        sx={custom.components.widgets.studioBodyRow}
      >
        <Paper
          elevation={0}
          sx={custom.components.widgets.studioNavPaper}
        >
          <Box sx={custom.components.widgets.studioNavHeader}>
            <Typography variant="overline" fontWeight={700} color="text.secondary">
              {t("widgetEditStudio.workspace")}
            </Typography>
          </Box>
          <List dense disablePadding sx={custom.components.widgets.studioNavList}>
            {navItems.map((item) => {
              const selected = activeSection === item.id;
              return (
                <ListItemButton
                  key={item.id}
                  selected={selected}
                  onClick={() => onSectionChange(item.id)}
                  sx={custom.components.widgets.studioNavItem(selected)}
                >
                  <ListItemIcon sx={custom.components.widgets.studioNavIcon(selected)}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={t(item.labelKey)}
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: selected ? 700 : 500,
                      fontSize: 13,
                      color: "inherit",
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Paper>

        <Box
          sx={custom.components.widgets.studioContent(isPreviewSection)}
        >
          {isPreviewSection ? content : <Stack spacing={1.5}>{content}</Stack>}
        </Box>
      </Box>
    </Box>
  );
}
